import React, { useMemo, useRef, useEffect, useCallback } from 'react';
import * as yup from 'yup';
import {
  ExtraFieldsForm,
  ExtraFieldsSupportedObject,
} from '../../../../components/ExtraFields';
import { ExtraFieldsFormId } from '../../../../components/ExtraFields/types';
import { getFormValuesFromExtraFields } from '../../../../components/ExtraFields/logic';
import {
  useExpenseByIdQuery,
  FieldType,
  Expense,
} from '../../../../__generated__/graphql';
import { ExpenseWrapperProps, ContextParam } from '../types';
import {
  ExtraFieldInternalValue,
  mapToExtraFieldInternalValue,
} from '../../../../components/ExtraFields/extraFieldInternalValue';
import { ExpenseRecordType } from '../../types';

type ExpenseFormWrapperOptions<T> = {
  wrapperProps: ExpenseWrapperProps;
  getDefaultValues: (expenseData: Expense | null, expenseGroupId?: string) => T;
  getValidationSchema: () => yup.ObjectSchema<any>;
  children: React.ReactElement;
  recordType: ExpenseRecordType;
};

export function ExpenseFormWrapper<T extends Record<string, any>>({
  wrapperProps,
  getDefaultValues,
  getValidationSchema,
  children,
  recordType,
}: ExpenseFormWrapperOptions<T>) {
  const { expenseId, expenseGroupId } = wrapperProps;

  const { data: expenseData } = useExpenseByIdQuery({
    variables: { id: expenseId || '' },
    skip: !expenseId,
  });

  const hasInitializedFormRef = useRef(false);
  const initialDefaultValuesRef = useRef<T | null>(null);
  const lastExpenseIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (lastExpenseIdRef.current !== expenseId) {
      hasInitializedFormRef.current = false;
      initialDefaultValuesRef.current = null;
      lastExpenseIdRef.current = expenseId || null;
    }
  }, [expenseId]);

  const defaultValues = useMemo<T>(() => {
    if (hasInitializedFormRef.current && initialDefaultValuesRef.current) {
      return initialDefaultValuesRef.current;
    }

    if (!expenseData?.expense) {
      const defaultVals = getDefaultValues(null, expenseGroupId);
      hasInitializedFormRef.current = true;
      initialDefaultValuesRef.current = defaultVals;
      return defaultVals;
    }

    const expense = expenseData.expense;
    const values = getFormValuesFromExtraFields(
      expense.extraFields.map(mapToExtraFieldInternalValue),
      recordType,
    );

    const calculatedValues = getDefaultValues(
      expense as Expense,
      expenseGroupId,
    );
    const finalValues = { ...values, ...calculatedValues };

    hasInitializedFormRef.current = true;
    initialDefaultValuesRef.current = finalValues;
    return finalValues;
  }, [expenseData, expenseGroupId, getDefaultValues, recordType]);

  const validationSchema = useMemo(() => {
    return getValidationSchema();
  }, [getValidationSchema]);

  const handleSaveFieldValues = useCallback(
    (_formData: T, _extraFields: ExtraFieldInternalValue[]) => {
      // Form submission is handled by auto-save logic in form content components
    },
    [],
  );

  const contextParams = useMemo<ContextParam[]>(
    () =>
      expenseData?.expense?.recordType?.Id
        ? [
            {
              name: 'recordTypeId',
              type: FieldType.String,
              value: expenseData.expense.recordType.Id,
            },
          ]
        : [],
    [expenseData?.expense?.recordType?.Id],
  );

  return (
    <ExtraFieldsForm<T>
      objectApiName={ExtraFieldsSupportedObject.Expense}
      formId={ExtraFieldsFormId.ExpensesDetails}
      onSubmit={handleSaveFieldValues}
      validationSchema={validationSchema}
      defaultValues={defaultValues}
      contextParams={contextParams}>
      {children}
    </ExtraFieldsForm>
  );
}
